import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiPaperclip, FiX, FiExternalLink, FiAlertTriangle, FiEdit2 } from 'react-icons/fi';
import MainLayout from '@shared/ui/main-layout';
import DataTable, { Column } from '@shared/ui/data-table';
import { Button } from '@shared/ui/button';
import { Select, SelectOption } from '@shared/ui/select';
import { SettingsToggle } from '@shared/ui/settings-toggle';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import { selectUser } from '../../entities/auth/model';
import {
  fetchTicketById,
  updateTicket,
  addTicketMessageWithFiles,
  resetCurrentTicket,
} from '../../entities/ticket/model';
import { useTicketStatusDict } from '../../shared/lib/ticket-statuses';
import { setErrorNotification, setNotification } from '../../app/store/notifications';
import { diagnosticsApi } from '../../api/services/diagnostics.service';
import hexConverter from '@entities/issue/lib/hex-converter';
import getTime from '@entities/issue/lib/get-time';
import {
  isDiagnosticsStaff,
  kindOfTicketType,
  formatDate,
  serialOrNone,
} from '@entities/diagnostics/lib';
import { DeviceResultModal } from '@features/diagnostic-device-result';
import {
  CONDITION_LABELS,
  DEFECT_LABELS,
  DIAG_FINAL_STATUSES,
  DIAG_STATUS_FLOW,
  DISPOSITION_LABELS,
  RESPONSIBLE_LABELS,
  WARRANTY_LABELS,
} from '../../types/diagnosticsInterfaces';
import type {
  DiagnosticDevice,
  DiagnosticTicket as DiagnosticTicketType,
  DiagnosticTypeInfo,
  SerialDuplicate,
} from '../../types/diagnosticsInterfaces';
import type { TicketMessage } from '../../types/ticketInterfaces';

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const personName = (u: any): string => {
  if (!u) return '—';
  return [u.last_name, u.first_name].filter(Boolean).join(' ') || u.username || '—';
};

const Chip = ({ label, tone = 'gray' }: { label: string; tone?: 'gray' | 'green' | 'red' | 'amber' | 'blue' }) => {
  const tones: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone]}`}>
      {label}
    </span>
  );
};

const warrantyTone = (w: DiagnosticDevice['warranty_status']) =>
  w === 'warranty' ? 'green' : w === 'non_warranty' || w === 'expired' ? 'red' : 'gray';

export default function DiagnosticTicket() {
  const { id } = useParams<{ id: string }>();
  const ticketId = Number(id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);
  const isStaff = isDiagnosticsStaff(me);
  const statusDict = useTicketStatusDict();

  // переписка/статус — из общего хелпдеск-слайса (это тот же тикет)
  const hdTicket = useAppSelector((s) => s.tickets.currentTicket);
  const hdStatus = useAppSelector((s) => s.tickets.status);

  const [diag, setDiag] = useState<DiagnosticTicketType | null>(null);
  const [types, setTypes] = useState<DiagnosticTypeInfo[]>([]);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState<DiagnosticDevice | null>(null);
  const [duplicates, setDuplicates] = useState<SerialDuplicate[]>([]);
  const [statusSaving, setStatusSaving] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);

  // композер сообщения
  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadDiag = useCallback(async () => {
    try {
      const { data } = await diagnosticsApi.getTicketById(ticketId);
      setDiag(data);
      setLoadError('');
    } catch (e: any) {
      setLoadError(e?.response?.data?.detail || 'Обращение не найдено');
    }
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    loadDiag();
    dispatch(fetchTicketById(ticketId));
    diagnosticsApi.getTypes().then((r) => setTypes(r.data)).catch(() => setTypes([]));
    return () => {
      dispatch(resetCurrentTicket());
    };
  }, [ticketId, dispatch, loadDiag]);

  // дедуп: серийники этого тикета в других тикетах (только персонал)
  useEffect(() => {
    if (!isStaff || !diag) return;
    const serials = diag.devices.map((d) => d.serial_number).filter((s): s is string => Boolean(s && s.trim()));
    if (!serials.length) {
      setDuplicates([]);
      return;
    }
    let cancelled = false;
    Promise.all(serials.map((s) => diagnosticsApi.lookupSerial(s, ticketId).then((r) => r.data.duplicates).catch(() => [])))
      .then((lists) => {
        if (cancelled) return;
        const seen = new Set<number>();
        const merged: SerialDuplicate[] = [];
        lists.flat().forEach((d) => {
          if (!seen.has(d.ticket_id)) {
            seen.add(d.ticket_id);
            merged.push(d);
          }
        });
        setDuplicates(merged);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, diag, ticketId]);

  const kind = kindOfTicketType(types, diag?.ticket_type?.id);
  const currentStatus = hdTicket?.status ?? diag?.status ?? '';
  const isFinal = DIAG_FINAL_STATUSES.has(currentStatus);

  const nextStatusOptions: SelectOption[] = useMemo(() => {
    if (!kind) return [];
    const next = DIAG_STATUS_FLOW[kind][currentStatus] ?? [];
    return next.map((code) => ({ value: code, label: statusDict.labelFor(code) }));
  }, [kind, currentStatus, statusDict]);

  const handleSetStatus = async (code: string) => {
    if (!code || code === currentStatus) return;
    setStatusSaving(true);
    try {
      await dispatch(updateTicket({ id: ticketId, data: { status: code } })).unwrap();
      dispatch(setNotification(`Статус: ${statusDict.labelFor(code)}`));
      await loadDiag();
    } catch (e: any) {
      dispatch(setErrorNotification(typeof e === 'string' ? e : 'Не удалось сменить статус'));
    } finally {
      setStatusSaving(false);
    }
  };

  const handleNotifyToggle = async (checked: boolean) => {
    setNotifySaving(true);
    try {
      const { data } = await diagnosticsApi.patchCase(ticketId, { notify_client: checked });
      setDiag((prev) => (prev ? { ...prev, notify_client: data.notify_client } : prev));
      dispatch(setNotification(checked ? 'Оповещения клиента включены' : 'Оповещения клиента выключены'));
    } catch {
      dispatch(setErrorNotification('Не удалось изменить оповещения'));
    } finally {
      setNotifySaving(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    if (!body.trim() && files.length === 0) return;
    setSending(true);
    try {
      await dispatch(
        addTicketMessageWithFiles({ ticketId, body: body.trim(), files, isInternal: isStaff && isInternal }),
      ).unwrap();
      setBody('');
      setFiles([]);
      setIsInternal(false);
      dispatch(fetchTicketById(ticketId));
    } catch (e: any) {
      dispatch(setErrorNotification(typeof e === 'string' ? e : 'Не удалось отправить сообщение'));
    } finally {
      setSending(false);
    }
  };

  const deviceColumns: Column<DiagnosticDevice>[] = useMemo(() => {
    const cols: Column<DiagnosticDevice>[] = [
      {
        key: 'model',
        header: 'Модель',
        render: (d) => (
          <div className="min-w-0">
            <p className="font-medium text-[#2d2c2c]">{d.model_display}</p>
            {d.client_reason ? (
              <p className="text-xs text-gray-400 truncate max-w-[260px]" title={d.client_reason}>
                {d.client_reason}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'serial',
        header: 'Серийный №',
        render: (d) => <span className="font-mono text-xs text-[#2d2c2c]">{serialOrNone(d.serial_number)}</span>,
      },
      {
        key: 'received',
        header: 'Поступил',
        render: (d) => <span className="text-xs text-gray-600">{formatDate(d.received_at)}</span>,
      },
      {
        key: 'result',
        header: 'Результат',
        render: (d) => (
          <div className="flex flex-wrap gap-1">
            <Chip label={WARRANTY_LABELS[d.warranty_status]} tone={warrantyTone(d.warranty_status)} />
            {d.condition !== 'unknown' && (
              <Chip label={CONDITION_LABELS[d.condition]} tone={d.condition === 'ok' ? 'green' : 'red'} />
            )}
            {d.disposition !== 'none' && <Chip label={DISPOSITION_LABELS[d.disposition]} tone="blue" />}
          </div>
        ),
      },
      {
        key: 'reason',
        header: 'Причина',
        render: (d) => (
          <span className="text-xs text-gray-700 truncate max-w-[220px] inline-block" title={d.reason_display}>
            {d.reason_display || '—'}
          </span>
        ),
      },
    ];
    if (isStaff) {
      cols.push(
        {
          key: 'party',
          header: 'Виновник / брак',
          render: (d) => (
            <span className="text-xs text-gray-600">
              {RESPONSIBLE_LABELS[d.responsible_party]}
              {d.defect_type !== 'none' ? ` · ${DEFECT_LABELS[d.defect_type]}` : ''}
            </span>
          ),
        },
        {
          key: 'edit',
          header: '',
          headerClassName: 'w-[1%]',
          stopRowClick: true,
          render: (d) => (
            <button
              type="button"
              title="Внести результат"
              onClick={() => setEditing(d)}
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-[#2d2c2c] hover:bg-gray-100"
            >
              <FiEdit2 />
            </button>
          ),
        },
      );
    }
    return cols;
  }, [isStaff]);

  const messages: TicketMessage[] = useMemo(() => {
    const list = hdTicket?.messages ?? [];
    return isStaff ? list : list.filter((m) => !m.is_internal);
  }, [hdTicket, isStaff]);

  if (loadError) {
    return (
      <MainLayout>
        <MainLayout.Title>Диагностика</MainLayout.Title>
        <MainLayout.Main>
          <div className="bg-white rounded-xl shadow-sm p-6 text-sm text-red-600">{loadError}</div>
        </MainLayout.Main>
      </MainLayout>
    );
  }

  const statusColor = statusDict.colorFor(currentStatus);

  return (
    <MainLayout>
      {/* MainLayout.Title в этом проекте — no-op (заголовок живёт в глобальной шапке),
          поэтому тему и номер заявки рендерим в карточке ниже. */}
      <MainLayout.Main>
        {/* ── шапка ── */}
        <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
          <button
            type="button"
            onClick={() => navigate('/devices')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#2d2c2c] mb-3"
          >
            <FiArrowLeft /> К списку заявок
          </button>
          <h2 className="text-xl font-semibold text-[#2d2c2c] mb-3 break-words">
            {diag ? (
              <>
                <span className="text-gray-400 font-normal mr-2">#{diag.number}</span>
                {diag.subject || 'Без темы'}
              </>
            ) : (
              'Загрузка…'
            )}
          </h2>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {currentStatus && (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  style={{ color: statusColor, backgroundColor: hexConverter(statusColor) }}
                >
                  {statusDict.labelFor(currentStatus)}
                </span>
              )}
              {diag?.ticket_type && <Chip label={diag.ticket_type.name} tone="blue" />}
              {diag?.company?.name ? (
                <span className="text-sm text-gray-600">{diag.company.name}</span>
              ) : diag?.sender_email ? (
                <span className="text-sm text-gray-600">{diag.sender_email}</span>
              ) : null}
              {isStaff && (
                <span className="text-sm text-gray-500">
                  · Ответственный: {diag?.assigned_to?.username ?? personName(hdTicket?.assigned_to)}
                </span>
              )}
              <span className="text-sm text-gray-400">· {formatDate(diag?.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              {isStaff && !isFinal && nextStatusOptions.length > 0 && (
                <div className="w-[260px]">
                  <Select
                    isVisible={false}
                    isClearable={false}
                    placeholder={statusSaving ? 'Сохранение…' : 'Перевести в статус…'}
                    customStyles={{ control: () => ({ height: '40px', minHeight: '40px' }) }}
                    options={nextStatusOptions}
                    optionSelected={null}
                    onSelected={(o) => o && handleSetStatus(String(o.value))}
                  />
                </div>
              )}
              <Link
                to={`/helpdesk/${ticketId}`}
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#2d2c2c]"
                title="Полная карточка: назначение, история, SLA"
              >
                <FiExternalLink /> Helpdesk
              </Link>
            </div>
          </div>

          {isStaff && kind === 'remote' && diag && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <SettingsToggle
                label="Оповещать клиента"
                description="Пока выключено — ни статусы, ни комментарии наружу не уходят."
                checked={Boolean(diag.notify_client)}
                disabled={notifySaving}
                onChange={handleNotifyToggle}
              />
            </div>
          )}

          {isStaff && duplicates.length > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <FiAlertTriangle className="mt-0.5 shrink-0" />
              <div>
                Серийные номера из этого обращения уже встречались:{' '}
                {duplicates.map((d, i) => (
                  <React.Fragment key={d.ticket_id}>
                    {i > 0 && ', '}
                    <Link to={`/devices/diagnostics/${d.ticket_id}`} className="underline font-medium">
                      {d.ticket_number ?? `#${d.ticket_id}`}
                    </Link>
                    {d.ticket_type ? ` (${d.ticket_type})` : ''}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── устройства ── */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
          <div className="px-6 pt-4 pb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#2d2c2c]">
              Оборудование <span className="text-gray-400 font-normal">({diag?.devices.length ?? 0})</span>
            </h3>
          </div>
          <div className="px-6 pb-4">
            <DataTable<DiagnosticDevice>
              columns={deviceColumns}
              data={diag?.devices ?? []}
              keyAccessor={(d) => d.id}
              onRowClick={isStaff ? (d) => setEditing(d) : undefined}
              loading={!diag}
              emptyMessage="Устройства не указаны"
            />
          </div>
        </div>

        {/* ── переписка ── */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#2d2c2c] mb-3">Переписка</h3>

          {hdStatus === 'loading' && !hdTicket ? (
            <p className="text-sm text-gray-400">Загрузка…</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-gray-400">Сообщений пока нет</p>
          ) : (
            <div className="flex flex-col gap-3 mb-4">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-xl px-4 py-3 border ${
                    m.is_internal ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-medium text-[#2d2c2c]">
                      {personName(m.author)}
                      {m.is_internal && <span className="ml-2 text-amber-700">· внутренняя заметка</span>}
                    </span>
                    <span className="text-xs text-gray-400">{getTime(m.created_at)}</span>
                  </div>
                  {m.body && <p className="text-sm text-[#2d2c2c] whitespace-pre-wrap">{m.body}</p>}
                  {m.attachments?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {m.attachments.map((a) => (
                        <a
                          key={a.id}
                          href={a.file}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-700 bg-white border border-gray-200 rounded-lg px-2 py-1 hover:underline"
                        >
                          <FiPaperclip /> {a.filename}
                          <span className="text-gray-400">({formatFileSize(a.size_bytes)})</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isFinal && (
            <div className="border-t border-gray-100 pt-4">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={isStaff ? 'Сообщение клиенту или внутренняя заметка…' : 'Ваше сообщение…'}
                className="w-full min-h-[90px] px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#2d2c2c] focus:outline-none focus:border-gray-400"
              />
              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <span
                      key={`${f.name}-${i}`}
                      className="inline-flex items-center gap-1 text-xs bg-gray-100 rounded-lg px-2 py-1"
                    >
                      <FiPaperclip /> {f.name}
                      <span className="text-gray-400">({formatFileSize(f.size)})</span>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="text-gray-400 hover:text-red-500"
                        title="Убрать"
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-1 text-sm text-gray-600 cursor-pointer">
                    <FiPaperclip />
                    <span>Прикрепить файл (счёт, акт…)</span>
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
                  </label>
                  {isStaff && (
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                      />
                      Внутренняя заметка
                    </label>
                  )}
                </div>
                <Button onClick={() => { void handleSend(); }} disabled={sending || (!body.trim() && files.length === 0)}>
                  {sending ? 'Отправка…' : 'Отправить'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </MainLayout.Main>

      <DeviceResultModal
        ticketId={ticketId}
        device={editing}
        onClose={() => setEditing(null)}
        onSaved={(d) => {
          setEditing(null);
          setDiag((prev) =>
            prev ? { ...prev, devices: prev.devices.map((x) => (x.id === d.id ? d : x)) } : prev,
          );
        }}
      />
    </MainLayout>
  );
}
